// ✅ src/screens/DownloadAppScreen.js (FULL)
// No "Install" button.
// Shows full detailed instructions for BOTH iPhone + Android
// Includes YouTube video thumbnails that open YouTube when clicked.

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

export default function DownloadAppScreen() {
  const nav = useNavigate();

  // ✅ Put your preferred YouTube video IDs here
  // (Thumbnail will auto-load from YouTube)
  const IOS_VIDEO_ID = "7l8o6DzLg0o";
  const ANDROID_VIDEO_ID = "MnBhHFpoyuo";

  const iosVideoUrl = useMemo(
    () => `https://www.youtube.com/watch?v=${IOS_VIDEO_ID}`,
    [IOS_VIDEO_ID]
  );
  const androidVideoUrl = useMemo(
    () => `https://www.youtube.com/watch?v=${ANDROID_VIDEO_ID}`,
    [ANDROID_VIDEO_ID]
  );

  const iosThumb = useMemo(
    () => `https://img.youtube.com/vi/${IOS_VIDEO_ID}/hqdefault.jpg`,
    [IOS_VIDEO_ID]
  );
  const androidThumb = useMemo(
    () => `https://img.youtube.com/vi/${ANDROID_VIDEO_ID}/hqdefault.jpg`,
    [ANDROID_VIDEO_ID]
  );

  function openLink(url) {
    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      window.location.href = url;
    }
  }

  return (
    <div style={S.wrap}>
      {/* Header */}
      <div style={S.header}>
        <button type="button" style={S.back} onClick={() => nav(-1)}>
          ←
        </button>
        <div>
          <div style={S.h1}>Download App</div>
          <div style={S.sub}>
            This is a website — you “save it” to your phone so it looks like an app.
          </div>
        </div>
      </div>

      {/* iPhone (Safari) */}
      <div style={S.card}>
        <div style={S.sectionTop}>
          <div style={S.badge}>📱 iPhone</div>
          <div>
            <div style={S.title}>How to add this site to your Home Screen (Safari)</div>
            <div style={S.muted}>
              Important: it must be <b>Safari</b> 🧭 — not Chrome.
            </div>
          </div>
        </div>

        {/* Step cards */}
        <div style={S.steps}>
          <div style={S.step}>
            <div style={S.stepNum}>1</div>
            <div style={S.stepText}>
              Open <b>Safari</b> 🧭 and go to this website.
              <div style={S.stepHint}>
                If you’re inside Instagram/TikTok browser, copy the link and paste it into Safari.
              </div>
            </div>
          </div>

          <div style={S.step}>
            <div style={S.stepNum}>2</div>
            <div style={S.stepText}>
              Tap the <b>Share</b> button ⬆️📤 (square with an arrow).
              <div style={S.stepHint}>
                You’ll see it at the bottom or top of Safari depending on your phone.
              </div>
            </div>
          </div>

          <div style={S.step}>
            <div style={S.stepNum}>3</div>
            <div style={S.stepText}>
              Scroll down and tap <b>Add to Home Screen</b> ➕🏠
              <div style={S.stepHint}>
                If you don’t see it, scroll more — it’s in the Share menu list.
              </div>
            </div>
          </div>

          <div style={S.step}>
            <div style={S.stepNum}>4</div>
            <div style={S.stepText}>
              Tap <b>Add</b> ✅ (top right).
              <div style={S.stepHint}>
                Done — it appears on your Home Screen like an app icon.
              </div>
            </div>
          </div>
        </div>

        {/* Video thumbnail */}
        <div style={S.videoCard}>
          <div style={S.videoTop}>
            <div style={S.videoTitle}>Watch video (iPhone)</div>
            <button type="button" style={S.linkBtn} onClick={() => openLink(iosVideoUrl)}>
              Open on YouTube
            </button>
          </div>

          <button
            type="button"
            style={S.thumbBtn}
            onClick={() => openLink(iosVideoUrl)}
            aria-label="Open iPhone tutorial video"
          >
            <img src={iosThumb} alt="iPhone tutorial thumbnail" style={S.thumbImg} />
            <div style={S.playBadge}>▶</div>
          </button>

          <div style={S.muted}>Click the thumbnail ▶ to watch exactly where to tap in Safari.</div>
        </div>
      </div>

      {/* Android (Chrome) */}
      <div style={S.card}>
        <div style={S.sectionTop}>
          <div style={S.badge}>🤖 Android</div>
          <div>
            <div style={S.title}>How to add this site to your Home Screen (Chrome)</div>
            <div style={S.muted}>
              Use <b>Chrome</b> 🌈 for best results.
            </div>
          </div>
        </div>

        {/* Step cards */}
        <div style={S.steps}>
          <div style={S.step}>
            <div style={S.stepNum}>1</div>
            <div style={S.stepText}>
              Open <b>Chrome</b> 🌈 and go to this website.
              <div style={S.stepHint}>
                If it opens inside another app’s browser, tap “Open in Chrome” if you see it.
              </div>
            </div>
          </div>

          <div style={S.step}>
            <div style={S.stepNum}>2</div>
            <div style={S.stepText}>
              Tap the <b>3 dots</b> menu ⋮ (top right).
              <div style={S.stepHint}>That’s the main menu for Chrome.</div>
            </div>
          </div>

          <div style={S.step}>
            <div style={S.stepNum}>3</div>
            <div style={S.stepText}>
              Tap <b>Install app</b> ⬇️📲
              <div style={S.stepHint}>
                If you don’t see “Install app”, tap <b>Add to Home screen</b> ➕🏠 instead.
              </div>
            </div>
          </div>

          <div style={S.step}>
            <div style={S.stepNum}>4</div>
            <div style={S.stepText}>
              Tap <b>Add</b> / <b>Install</b> ✅ to confirm.
              <div style={S.stepHint}>Done — it will show on your home screen like an app.</div>
            </div>
          </div>
        </div>

        {/* Video thumbnail */}
        <div style={S.videoCard}>
          <div style={S.videoTop}>
            <div style={S.videoTitle}>Watch video (Android)</div>
            <button type="button" style={S.linkBtn} onClick={() => openLink(androidVideoUrl)}>
              Open on YouTube
            </button>
          </div>

          <button
            type="button"
            style={S.thumbBtn}
            onClick={() => openLink(androidVideoUrl)}
            aria-label="Open Android tutorial video"
          >
            <img src={androidThumb} alt="Android tutorial thumbnail" style={S.thumbImg} />
            <div style={S.playBadge}>▶</div>
          </button>

          <div style={S.muted}>Click the thumbnail ▶ to watch exactly where to tap in Chrome.</div>
        </div>
      </div>

      {/* Tiny note (non-technical) */}
      <div style={S.card}>
        <div style={S.title}>Quick note</div>
        <div style={S.muted}>
          After you add it, you can open it from your Home Screen anytime like a normal app.
        </div>
      </div>
    </div>
  );
}

/* ---------------- styles ---------------- */
const S = {
  wrap: { display: "grid", gap: 12 },

  header: {
    display: "grid",
    gridTemplateColumns: "44px 1fr",
    gap: 10,
    alignItems: "center",
  },
  back: {
    width: 44,
    height: 44,
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.08)",
    background: "rgba(255,255,255,0.75)",
    boxShadow: "0 12px 28px rgba(2,6,23,0.06)",
    cursor: "pointer",
    fontSize: 18,
    fontWeight: 1000,
  },
  h1: { fontSize: 18, fontWeight: 1000 },
  sub: { opacity: 0.7, fontSize: 13, marginTop: 2, lineHeight: 1.35 },

  card: {
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.07)",
    background: "rgba(255,255,255,0.70)",
    boxShadow: "0 14px 34px rgba(2,6,23,0.06)",
    padding: 14,
    display: "grid",
    gap: 12,
  },

  sectionTop: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: 10,
    alignItems: "center",
  },
  badge: {
    padding: "8px 10px",
    borderRadius: 14,
    border: "1px solid rgba(2,6,23,0.08)",
    background: "rgba(255,255,255,0.75)",
    fontWeight: 1000,
    fontSize: 13,
  },

  title: { fontWeight: 1000, fontSize: 14 },
  muted: { opacity: 0.75, fontSize: 13, lineHeight: 1.35 },

  steps: { display: "grid", gap: 8 },
  step: {
    display: "grid",
    gridTemplateColumns: "34px 1fr",
    gap: 10,
    alignItems: "start",
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.06)",
    background: "rgba(255,255,255,0.75)",
    padding: 10,
  },
  stepNum: {
    width: 34,
    height: 34,
    borderRadius: 14,
    display: "grid",
    placeItems: "center",
    background: "rgba(17,24,39,0.08)",
    border: "1px solid rgba(2,6,23,0.06)",
    fontWeight: 1000,
  },
  stepText: { fontSize: 13, lineHeight: 1.35 },
  stepHint: { marginTop: 4, opacity: 0.75, fontSize: 12, lineHeight: 1.35 },

  videoCard: {
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.06)",
    background: "rgba(255,255,255,0.75)",
    padding: 10,
    display: "grid",
    gap: 8,
  },
  videoTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  videoTitle: { fontWeight: 1000, fontSize: 13 },

  linkBtn: {
    border: "1px solid rgba(2,6,23,0.10)",
    background: "rgba(255,255,255,0.85)",
    borderRadius: 14,
    padding: "10px 12px",
    fontWeight: 1000,
    cursor: "pointer",
    fontSize: 12,
  },

  thumbBtn: {
    position: "relative",
    padding: 0,
    cursor: "pointer",
    borderRadius: 14,
    overflow: "hidden",
    width: "100%",
    background: "transparent",
    border: "1px solid rgba(2,6,23,0.08)",
    boxShadow: "0 12px 28px rgba(2,6,23,0.06)",
  },
  thumbImg: { width: "100%", height: 200, objectFit: "cover", display: "block" },
  playBadge: {
    position: "absolute",
    inset: 0,
    display: "grid",
    placeItems: "center",
    fontSize: 44,
    fontWeight: 1000,
    color: "white",
    textShadow: "0 10px 30px rgba(0,0,0,0.45)",
    background: "linear-gradient(to bottom, rgba(0,0,0,0.05), rgba(0,0,0,0.22))",
  },
};